import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  List,
  ListItemButton,
  ListItemText,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import { Link as RouterLink, useParams } from "react-router-dom";

import { useCurrentPermissions } from "../../contexts/PermissionsProvider";
import AssignmentPickerDialog from "../../components/Directory/AssignmentPickerDialog";
import {
  DetailShell,
  EmptyState,
  ScopeBadge,
  SectionCard,
  SkeletonTable,
  StatusPill,
} from "../../components/Directory/primitives";
import { getUser, removeGroupMember, revokeRoleAssignment } from "./api";

/**
 * DirectoryUserDetail — /directory/users/:id.
 *
 * Overview surface with the four sections a Users-tab row should
 * drill into:
 *   * Identity — email, name, source (native/google), user_type
 *   * Accounts — accounts this user is a member of
 *   * Groups — groups this user belongs to
 *   * Roles — role assignments (via the shared AssignmentPickerDialog)
 *
 * Uses the shared DetailShell primitive for the hero + breadcrumb, and
 * SectionCard for the four body sections, so the drift between this
 * page and RoleDetail's hand-rolled shells is gone.
 */
function initialsOf(u) {
  const src = u?.name || u?.email || "?";
  const parts = src.split(/[.\s@_-]/).filter(Boolean);
  const bits = parts.slice(0, 2).map((s) => s[0]?.toUpperCase() || "?");
  return bits.join("") || "?";
}

export default function DirectoryUserDetail() {
  const { id } = useParams();
  const { permissions } = useCurrentPermissions();
  const isSystem =
    (permissions || []).includes("SYSTEM") ||
    (permissions || []).includes("SYSTEM_DIRECTORY_ADMIN");

  const [user, setUser] = useState(null);
  const [err, setErr] = useState(null);
  const [toast, setToast] = useState(null);

  const [addGroupOpen, setAddGroupOpen] = useState(false);
  const [assignRoleOpen, setAssignRoleOpen] = useState(false);

  const load = useCallback(() => {
    setErr(null);
    getUser(id)
      .then(setUser)
      .catch((e) =>
        setErr(
          e.status === 403
            ? "You don't have permission to view this user."
            : e.status === 404
              ? "User not found."
              : `Failed to load user: ${e.message}`
        )
      );
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // Prefer explicit accounts[] shape; fall back to legacy single-account.
  const accounts = Array.isArray(user?.accounts)
    ? user.accounts
    : user?.accountId
      ? [{ id: user.accountId, name: user.accountName }]
      : [];
  const groups = Array.isArray(user?.groups) ? user.groups : [];
  const roles = Array.isArray(user?.roles) ? user.roles : [];

  // Primary account used to route user→group and role assignments.
  const scopeAccountId = accounts[0]?.id || null;

  const removeFromGroup = async (group) => {
    try {
      await removeGroupMember({
        scopeAccountId: group.accountId || scopeAccountId,
        gid: group.id,
        userId: id,
      });
      setToast({ message: `Removed from ${group.name || "group"}.` });
      load();
    } catch (e) {
      setErr(
        `Failed to remove from group. HTTP ${e.status || "?"} — try again in a moment.`
      );
    }
  };

  const revokeRole = async (role) => {
    try {
      if (role.accountId || scopeAccountId) {
        await revokeRoleAssignment({
          accountId: role.accountId || scopeAccountId,
          subjectType: "USER",
          subjectId: id,
          roleId: role.id,
        });
      } else {
        setErr(
          "Platform-wide role revoke requires an account scope; not supported yet."
        );
        return;
      }
      setToast({ message: `Revoked ${role.name || "role"}.` });
      load();
    } catch (e) {
      setErr(
        `Failed to revoke role. HTTP ${e.status || "?"} — try again in a moment.`
      );
    }
  };

  const loading = !user && !err;

  return (
    <DetailShell
      backTo="/directory/users"
      parentLabel={isSystem ? "Users" : "Members"}
      parentTo="/directory/users"
      title={user?.name || user?.email || (loading ? "Loading…" : "User")}
      avatar={
        user && (
          <Avatar
            sx={{
              width: 48,
              height: 48,
              fontWeight: 700,
              bgcolor: "primary.main",
            }}
          >
            {initialsOf(user)}
          </Avatar>
        )
      }
      badges={
        user
          ? [
              <StatusPill
                key="s"
                status={user.enabled === false ? "disabled" : "active"}
              />,
              user.userType && user.userType !== "USER" ? (
                <Chip
                  key="t"
                  label={user.userType}
                  size="small"
                  color="secondary"
                  variant="outlined"
                />
              ) : null,
            ]
          : []
      }
      description={user?.email || (loading ? undefined : "—")}
    >
      {err && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {err}
        </Alert>
      )}

      {loading && (
        <SkeletonTable
          columns={[{ label: "Field" }, { label: "Value" }]}
          rows={4}
        />
      )}

      {user && (
        <Stack spacing={2}>
          <SectionCard title="Identity">
            <Stack spacing={0.5}>
              <KeyValue k="Email" v={user.email} />
              <KeyValue k="Name" v={user.name} />
              <KeyValue k="Type" v={user.userType || "USER"} />
              <KeyValue
                k="Source"
                v={user.authSource || (user.googleLinked ? "google" : "native")}
              />
              <KeyValue
                k="Status"
                v={user.enabled === false ? "Disabled" : "Active"}
              />
              <KeyValue k="User ID" v={user.id} mono />
            </Stack>
          </SectionCard>

          <SectionCard
            title="Accounts"
            count={accounts.length}
            isEmpty={accounts.length === 0}
            emptyState={{
              title: "Not a member of any account",
              description:
                "This user hasn't redeemed an invitation yet — the first account they join will appear here.",
            }}
          >
            <Stack spacing={0.5}>
              {accounts.map((a) => (
                <Stack
                  key={a.id}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ py: 0.5 }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <BusinessOutlinedIcon
                      fontSize="small"
                      sx={{ color: "text.disabled" }}
                    />
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {a.name || a.id}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {a.id}
                      </Typography>
                    </Box>
                  </Stack>
                  {a.role && (
                    <Chip label={a.role} size="small" variant="outlined" />
                  )}
                </Stack>
              ))}
            </Stack>
          </SectionCard>

          <SectionCard
            title="Groups"
            count={groups.length}
            action={
              <Tooltip
                title={
                  scopeAccountId || isSystem
                    ? "Grant this user access via group membership. Roles on the group apply automatically."
                    : "Cannot add to group — no account scope resolved yet."
                }
                arrow
                enterDelay={400}
              >
                <span>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<WorkspacesOutlinedIcon />}
                    disabled={!scopeAccountId && !isSystem}
                    onClick={() => setAddGroupOpen(true)}
                    sx={{ textTransform: "none" }}
                    aria-label="Add to group"
                  >
                    Add to group
                  </Button>
                </span>
              </Tooltip>
            }
            isEmpty={groups.length === 0}
            emptyState={{
              title: "Not in any group",
              description:
                "Use Add to group above to grant a role bundle via group membership — easier than assigning each role directly.",
            }}
          >
            <Stack spacing={0.5}>
              {groups.map((g) => (
                <GroupRow
                  key={g.id}
                  group={g}
                  onRemove={() => removeFromGroup(g)}
                />
              ))}
            </Stack>
          </SectionCard>

          <SectionCard
            title="Assigned roles"
            count={roles.length}
            action={
              <Tooltip
                title={
                  scopeAccountId
                    ? "Grant this user a role directly, at the account scope."
                    : "Cannot assign role — no account scope resolved yet."
                }
                arrow
                enterDelay={400}
              >
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ShieldOutlinedIcon />}
                    disabled={!scopeAccountId}
                    onClick={() => setAssignRoleOpen(true)}
                    sx={{ textTransform: "none" }}
                    aria-label="Assign role"
                  >
                    Assign role
                  </Button>
                </span>
              </Tooltip>
            }
            isEmpty={roles.length === 0}
            emptyState={{
              title: "No roles assigned directly",
              description:
                "Use Assign role above to grant entitlements to this user. Group membership also carries roles — check the Groups section.",
            }}
          >
            <Stack spacing={0.5}>
              {roles.map((r) => (
                <RoleRow
                  key={r.id}
                  role={r}
                  onRevoke={() => revokeRole(r)}
                />
              ))}
            </Stack>
          </SectionCard>
        </Stack>
      )}

      <AssignmentPickerDialog
        open={addGroupOpen}
        mode="add_to_group"
        userId={id}
        scopeAccountId={scopeAccountId}
        onClose={() => setAddGroupOpen(false)}
        onSaved={({ count }) => {
          setAddGroupOpen(false);
          setToast({ message: `Added to ${count} group(s).` });
          load();
        }}
      />

      <AssignRoleToUserDialog
        open={assignRoleOpen}
        userId={id}
        scopeAccountId={scopeAccountId}
        onClose={() => setAssignRoleOpen(false)}
        onSaved={({ count }) => {
          setAssignRoleOpen(false);
          setToast({ message: `Assigned ${count} role(s).` });
          load();
        }}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? (
          <Alert
            severity="success"
            onClose={() => setToast(null)}
            sx={{ width: "100%" }}
          >
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </DetailShell>
  );
}

function GroupRow({ group: g, onRemove }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        py: 0.75,
        px: 1,
        borderRadius: 1,
        "& .row-actions": {
          opacity: 0,
          transition: "opacity 150ms ease-in-out",
        },
        "&:hover, &:focus-within": {
          bgcolor: "action.hover",
          "& .row-actions": { opacity: 1 },
        },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
        <WorkspacesOutlinedIcon
          fontSize="small"
          sx={{ color: "text.disabled" }}
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={500} noWrap>
            <MuiLink
              component={RouterLink}
              to={`/directory/groups`}
              underline="hover"
              state={{ focusGroupId: g.id }}
            >
              {g.name || g.id}
            </MuiLink>
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {g.accountName || g.id}
          </Typography>
        </Box>
      </Stack>
      <Box className="row-actions">
        <Tooltip
          title="Remove — revokes access carried by this group immediately."
          arrow
          enterDelay={400}
        >
          <IconButton
            size="small"
            color="error"
            onClick={onRemove}
            aria-label={`Remove from ${g.name || "group"}`}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Stack>
  );
}

function RoleRow({ role: r, onRevoke }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        py: 0.75,
        px: 1,
        borderRadius: 1,
        "& .row-actions": {
          opacity: 0,
          transition: "opacity 150ms ease-in-out",
        },
        "&:hover, &:focus-within": {
          bgcolor: "action.hover",
          "& .row-actions": { opacity: 1 },
        },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
        <ShieldOutlinedIcon fontSize="small" sx={{ color: "text.disabled" }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={500} noWrap>
            <MuiLink
              component={RouterLink}
              to={`/directory/roles/${r.id}`}
              underline="hover"
            >
              {r.name || r.id}
            </MuiLink>
          </Typography>
          {r.description && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {r.description}
            </Typography>
          )}
        </Box>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        {r.accountId ? (
          <ScopeBadge scope="account" name={r.accountName || r.accountId} />
        ) : (
          <ScopeBadge scope="system" />
        )}
        <Box className="row-actions">
          <Tooltip
            title="Remove — revokes this role immediately."
            arrow
            enterDelay={400}
          >
            <IconButton
              size="small"
              color="error"
              onClick={onRevoke}
              aria-label={`Revoke ${r.name || "role"}`}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Stack>
    </Stack>
  );
}

function KeyValue({ k, v, mono }) {
  return (
    <Stack direction="row" spacing={2} alignItems="baseline">
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ minWidth: 96 }}
      >
        {k}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontFamily: mono ? "monospace" : undefined, wordBreak: "break-all" }}
      >
        {v || "—"}
      </Typography>
    </Stack>
  );
}

/**
 * Assign one or more roles to this user via the account-scoped
 * /accounts/{aid}/role-assignments route.
 *
 * Rebuilt on top of the shared MUI Dialog + primitives so the
 * hand-rolled overlay + <input style={{border: "rgba(0,0,0,0.23)"}}>
 * that broke in dark mode is gone.
 */
function AssignRoleToUserDialog({
  open,
  userId,
  scopeAccountId,
  onClose,
  onSaved,
}) {
  const [roles, setRoles] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setQ("");
    setError(null);
    if (roles) return;
    import("./api").then(({ getRoles }) => {
      getRoles()
        .then(setRoles)
        .catch((e) => setError(`Failed to load roles: ${e.message}`));
    });
  }, [open, roles]);

  const filtered = (roles || []).filter((r) => {
    const n = q.trim().toLowerCase();
    if (!n) return true;
    return [r.name, r.description, r.id]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(n);
  });

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = async () => {
    if (!selected.size || !scopeAccountId) return;
    setBusy(true);
    setError(null);
    const errors = [];
    for (const rid of selected) {
      try {
        const { createRoleAssignment } = await import("./api");
        await createRoleAssignment({
          accountId: scopeAccountId,
          subjectType: "USER",
          subjectId: userId,
          roleId: rid,
        });
      } catch (e) {
        errors.push(`HTTP ${e.status || "?"} on ${rid.slice(0, 8)}…`);
      }
    }
    setBusy(false);
    if (errors.length) {
      setError(
        `${selected.size - errors.length}/${selected.size} assigned. ${errors.join("; ")}`
      );
    }
    if (errors.length < selected.size) {
      onSaved && onSaved({ count: selected.size - errors.length });
    }
  };

  return (
    <Dialog
      open={!!open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Assign role to this user</DialogTitle>
      <DialogContent dividers sx={{ px: 0 }}>
        <Box sx={{ px: 3, py: 2 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Filter roles"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        {roles === null && !error ? (
          <Box sx={{ px: 3, pb: 3 }}>
            <SkeletonTable
              columns={[{ label: "Role" }, { label: "Description" }]}
              rows={4}
            />
          </Box>
        ) : (
          <List dense sx={{ maxHeight: 320, overflow: "auto", py: 0 }}>
            {filtered.length === 0 && (
              <Box sx={{ px: 3, py: 2 }}>
                <EmptyState
                  compact
                  title={q ? "No matches" : "No roles available"}
                  description={
                    q
                      ? "Try clearing the filter or searching for a different name."
                      : "Create a role first, then come back to assign it."
                  }
                  variant={q ? "no-match" : "empty"}
                />
              </Box>
            )}
            {filtered.map((r) => {
              const checked = selected.has(r.id);
              return (
                <ListItemButton
                  key={r.id}
                  dense
                  onClick={() => toggle(r.id)}
                  selected={checked}
                >
                  <Checkbox
                    edge="start"
                    checked={checked}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{ "aria-label": r.name || r.id }}
                  />
                  <ListItemText
                    primary={r.name || r.id}
                    secondary={r.description || r.id}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}
        {error && (
          <Alert severity="error" sx={{ mx: 3, mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Box sx={{ mr: "auto" }}>
          <Chip
            size="small"
            label={`${selected.size} selected`}
            color={selected.size ? "primary" : "default"}
            variant="outlined"
          />
        </Box>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={busy || !selected.size || !scopeAccountId}
        >
          {busy ? "Assigning…" : "Assign"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
